import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getLiveOutputContext,
  readTtsVolume,
  requestAudioFocus,
  type AudioFocusHandle,
} from '@orborix/voice-client';

/**
 * Hook de reproducción de blobs de audio (audio/mpeg) on-demand. Encapsula el
 * patrón fetch→ObjectURL→`<audio>` con limpieza de URLs y estados de UI, usado
 * por el botón "Escuchar" de bubbles, la explicación hablada de correcciones y
 * el "Probar voz" del CoachPicker.
 *
 * `fetcher` se invoca al primer play (lazy); el blob NO se cachea entre plays
 * para no retener audio en memoria — el backend cachea el TTS server-side.
 *
 * Suena UNO a la vez: al apretar play se pide el foco de audio global
 * (`requestAudioFocus`), que calla lo que estuviera sonando — otra burbuja, el
 * coach en vivo, el player de Escuchar. El pedido va con el TAP, no cuando el
 * fetch termina: el tap es la señal de "quiero escuchar ESTO", y esperar a la
 * descarga dejaría los dos audios encimados mientras carga.
 *
 * El volumen de la voz del coach es UNA preferencia sola (`orborix.ttsVolume`):
 * la conversación en vivo la aplica en el gain del `AudioPlayer` y todo lo que
 * sale por acá la aplica en el `<audio>`. Si sólo la aplicara un lado, la misma
 * frase sonaría a distinto volumen según por dónde salga.
 *
 * Y con la charla viva el `<audio>` se enruta por el AudioContext de la sesión
 * (`getLiveOutputContext`) en vez de sonar por su cuenta. No es una
 * optimización: sonando solo, el clip sale por el canal de LLAMADAS del OS
 * (nace con el mic ya encendido) mientras el coach en vivo sale por el de
 * MULTIMEDIA, así que el usuario los maneja con dos perillas de volumen
 * distintas. Ver `AudioPlayer.getLiveOutputContext` para el mecanismo.
 */

export type AudioBtnState = 'idle' | 'loading' | 'playing' | 'error';

export interface UseAudioBlobReturn {
  state: AudioBtnState;
  /**
   * Lo que tiró el `fetcher` (o la reproducción) la última vez que `state`
   * pasó a `'error'`; `null` mientras no haya fallado.
   *
   * Existe porque el hook **se traga el error** y los call sites no tenían de
   * dónde sacar el motivo: el botón volvía a "Escuchar" sin decir nada. Con el
   * tope de 30 audios de palabra por minuto eso pasó a importar — un usuario
   * frenado veía un botón mudo. El hook no traduce nada (no conoce la API);
   * cada pantalla mapea el error a su copy (`describeSpeakError`).
   */
  error: unknown;
  /** Reproduce si idle/error; detiene si está sonando. */
  toggle: (fetcher: () => Promise<Blob>) => Promise<void>;
  /**
   * Reproduce `fetcher` YA, cortando lo que esté sonando o cargando (nunca se
   * limita a parar, a diferencia de `toggle`). Llamadas rápidas seguidas: sólo
   * suena la última — el token invalida los fetch en vuelo. Lo usa el
   * auto-play del CoachPicker al elegir una voz.
   */
  play: (fetcher: () => Promise<Blob>) => Promise<void>;
  /** Detiene la reproducción en curso (no-op si no suena). */
  stop: () => void;
  /**
   * Posición actual del audio en ms, o `-1` si no hay nada cargado. Es un
   * GETTER (no estado) para poder muestrearlo por `requestAnimationFrame` sin
   * re-renderizar en cada frame — lo usa el karaoke del replay de burbujas.
   * Identidad estable: se puede pasar como dep de efectos.
   */
  getPositionMs: () => number;
  /**
   * Duración del audio cargado en ms, o `-1` si no hay nada (o todavía no se
   * sabe). Getter como `getPositionMs`: para un karaoke proporcional cuando la
   * voz no trae tiempos por carácter (Gemini TTS).
   */
  getDurationMs: () => number;
}

export function useAudioBlob(): UseAudioBlobReturn {
  const [state, setState] = useState<AudioBtnState>('idle');
  const [error, setError] = useState<unknown>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const focusRef = useRef<AudioFocusHandle | null>(null);
  /**
   * Invalida el play en vuelo. Cada `toggle` toma un token; si al volver del
   * fetch el token cambió (nos desplazó otro audio, o el usuario apretó stop),
   * el blob se descarta en vez de empezar a sonar tarde encima de otra cosa.
   */
  const tokenRef = useRef(0);
  /**
   * Nodo que enchufa el `<audio>` al AudioContext de la sesión viva. Hay que
   * soltarlo al terminar: `createMediaElementSource` es irreversible por
   * elemento, así que cada play crea el suyo y lo desconecta acá.
   */
  const routeRef = useRef<MediaElementAudioSourceNode | null>(null);

  const teardown = (): void => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (routeRef.current) {
      routeRef.current.disconnect();
      routeRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  useEffect(
    () => () => {
      focusRef.current?.release();
      teardown();
    },
    [],
  );

  // Cambiar el volumen mientras algo suena lo mueve en el acto, igual que el
  // efecto que sincroniza el gain del audio en vivo.
  useEffect(() => {
    const onVolume = (e: Event): void => {
      const detail = (e as CustomEvent<number>).detail;
      if (typeof detail === 'number' && audioRef.current) audioRef.current.volume = detail;
    };
    window.addEventListener('orborix:pref.ttsVolume', onVolume);
    return () => window.removeEventListener('orborix:pref.ttsVolume', onVolume);
  }, []);

  const stop = (): void => {
    tokenRef.current += 1;
    focusRef.current?.release();
    focusRef.current = null;
    teardown();
    setError(null);
    setState('idle');
  };

  const toggle = async (fetcher: () => Promise<Blob>): Promise<void> => {
    if (state === 'playing') {
      stop();
      return;
    }
    if (state === 'loading') return;
    await start(fetcher);
  };

  const play = async (fetcher: () => Promise<Blob>): Promise<void> => {
    // Soltar NUESTRO foco antes de volver a pedirlo: si no, el árbitro llamaría
    // a nuestro propio `stop` (el dueño desplazado) e invalidaría el token del
    // play que está arrancando.
    focusRef.current?.release();
    focusRef.current = null;
    teardown();
    await start(fetcher);
  };

  const start = async (fetcher: () => Promise<Blob>): Promise<void> => {
    // El foco se pide con el tap: lo que sonaba se calla ya, sin esperar al
    // fetch. `stop` es cómo nos callamos si otro audio nos desplaza a nosotros.
    const token = ++tokenRef.current;
    focusRef.current = requestAudioFocus('speak:clip', stop);
    setError(null);
    setState('loading');
    try {
      const blob = await fetcher();
      if (token !== tokenRef.current) return;
      teardown();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      const audio = new Audio(url);
      audio.volume = readTtsVolume();
      audioRef.current = audio;
      // Con la charla viva, salir por SU contexto (mismo canal del OS que el
      // coach). Sin sesión no hay mic encendido y el `<audio>` suelto ya cae
      // en multimedia, así que no hay nada que corregir.
      const outputCtx = getLiveOutputContext();
      if (outputCtx) {
        try {
          const route = outputCtx.createMediaElementSource(audio);
          route.connect(outputCtx.destination);
          routeRef.current = route;
        } catch (err) {
          // Degrada a `<audio>` suelto: se escucha, pero por el canal de
          // llamadas. Ruidoso a propósito — es la falla que este código existe
          // para evitar, y en dev el log del browser llega al dev server.
          console.warn('[audio] no se pudo enrutar el clip por la salida viva', err);
        }
      }
      audio.addEventListener('ended', () => stop());
      audio.addEventListener('error', () => setState('error'));
      // (el `error` del `<audio>` no trae nada útil: `state` alcanza)
      await audio.play();
      if (token !== tokenRef.current) {
        teardown();
        return;
      }
      setState('playing');
    } catch (err) {
      if (token !== tokenRef.current) return;
      focusRef.current?.release();
      focusRef.current = null;
      setError(err);
      setState('error');
    }
  };

  const getPositionMs = useCallback((): number => {
    const audio = audioRef.current;
    if (!audio) return -1;
    return audio.currentTime * 1000;
  }, []);

  const getDurationMs = useCallback((): number => {
    const d = audioRef.current?.duration;
    return typeof d === 'number' && Number.isFinite(d) && d > 0 ? d * 1000 : -1;
  }, []);

  return { state, error, toggle, play, stop, getPositionMs, getDurationMs };
}
